<template>
	<div class="component" data-testid="details-component">
		<div v-show="!isServerError && !isWaiting">
			<section>
				<div class="container">
					<div class="row">
						<div class="col-xs-12 p-t-3">
							<h1 class="b-l b-thick p-l-sm branded" data-testid="eventTitle">{{ eventData.name }}</h1>
							<span v-if="eventData.skillLevel" class="badge badge-pill badge-default">{{ eventData.skillLevel }}</span>
							<span v-if="eventData.intensity" class="badge badge-pill badge-default">{{ eventData.intensity }}</span>
						</div>
					</div>
					<div class="row">
						<div :class="showCtaSidebar ? 'details-section col-xs-12 col-md-8 col-lg-9 m-y-2 b-r-md' : 'details-section col-xs-12 m-y-2'">
							<div class="row">
								<div v-show="eventData.image" class="col-xs-12 edge-xs-down col-sm-6 m-b-2">
									<img alt="" :src="eventData.image">
								</div>
								<div class="col-xs-12 m-b-2" :class="{'col-sm-6': eventData.image}">

									<p class="small text-gray-5 m-b-xs" v-if="display.recursStartMoment">{{ display.recursStartLabel }}: <TemporalDisplay :start="display.recursStartMoment" template="date"/></p>
									<p class="h3 m-b-xs" :class="{'text-danger': eventData.isCanceled}"><TemporalDisplay :start="display.startMoment" template="dateFull"/><span v-if="eventData.isCanceled"> (Canceled)</span></p>
									<p class="strong-start-time m-b-xs"><TemporalDisplay :start="display.startMoment" :end="display.endMoment" template="time"/></p>

									<p class="small text-gray-5 m-b-0" v-if="display.recursDays">Meets: {{ display.recursDays }}</p>
									<p class="small text-gray-5 m-b-grid" v-if="display.recursEndMoment">{{ display.recursEndLabel }}: <TemporalDisplay :start="display.recursEndMoment" template="date"/></p>

									<p class="text-gray-5 m-b-0 font-weight-bold" v-if="eventData.instructors">
										<span v-for="(instructor, index) in eventData.instructors" :key="index">
											<span v-if="instructor.subFor"><s>{{ instructor.subFor }}</s></span>
											<a v-if="instructor.bioUrl && !instructor.subFor && !isMobileApp" :href="instructor.bioUrl">{{ instructor.name }}</a>
											<a v-else-if="instructor.subFor && instructor.bioUrl && !isMobileApp" :href="instructor.bioUrl" class="text-danger">{{ instructor.name + ' (Sub)' }}</a>
											<span v-else-if="instructor.subFor && (!instructor.bioUrl || isMobileApp)" class="text-danger">{{ instructor.name + ' (Sub)' }}</span>
											<span v-else>{{ instructor.name }}</span>{{ index == eventData.instructors.length - 1 ? '' : (eventData.instructors.length == 2) ? ' and ' : ( index == eventData.instructors.length - 2) ? ', and ' : ', ' }}
										</span>
									</p>
									<p class="text-gray-5 m-b-grid">{{ display.location }}</p>

									<button v-if="showAddToCalendar" class="btn btn-primary-outline btn-sm btn-block-xs" data-testid="addToCalendarBtn" @click="addToCalendar()">Add to Calendar</button>

								</div>

							</div>


							<!-- CTA Section for Registration (XS and SM Breakpoints Only) -->
							<div v-show="showCtaSidebar" class="hidden-md-up b-y">
								<RegistrationSub
									v-if="isWaitingRegistration || regData"
									:regData="regData"
									:sessionsRemaining="eventData.sessionsRemaining"
									:costPrefix="eventData.costPrefix"
									:costVaries="eventData.costVaries"
									:costAdvertised="display.costAdvertised"
									:costSuffix="eventData.costSuffix"
									:userAuthenticated="userAuthenticated"
									:isWaitingRegistration="isWaitingRegistration"
									@ctaClicked="handleCtaClicked"
									@reserveCtaClicked="handleReserveCtaClicked"
								/>
							</div>

							<h2 class="base-font-size font-weight-bold p-t-2 m-b-sm" v-if="eventData.description">What to Expect</h2>
							<p>{{ eventData.description }}</p>

							<!-- Focus Tags Section -->
							<div v-for="i in focusTagsRowCount" :key="'focus'+i" class="focusTagsRow">
								<div v-for="item in itemCountInFocusTagsRow(i)" :key="item.heading" class="m-b-2">
									<h2 class="base-font-size font-weight-bold m-b-sm">{{ item.heading }}</h2>
									<div class="bar m-b-sm">
										<div class="bar-section" :class="{'is-full': item.value >= 1}"></div><div class="bar-section" :class="{'is-full': item.value >= 2}"></div><div class="bar-section" :class="{'is-full': item.value >= 3}"></div>
										<span class="sr-only">{{ item.value }} of 3</span>
									</div>
									<p v-if="item.description">{{ item.description }}</p>
								</div>
							</div>

							<!-- What to Know Tags Section -->
							<div v-for="i in whatToKnowTagsRowCount" :key="'whatToKnow'+i" class="whatToKnowRow">
								<div v-for="(item, index) in itemCountInWhatToKnowTagsRow(i)" :key="item.heading" :class="{'m-b-2': index !== itemCountInWhatToKnowTagsRow(i).length - 1}">
									<h2 class="base-font-size font-weight-bold m-b-sm">{{ item.heading }}</h2>
									<p>{{ item.value }}</p>
								</div>
							</div>

						</div>

						<!-- CTA Section for Registration (MD Breakpoint and Up) -->
						<div v-show="showCtaSidebar" class="hidden-sm-down col-xs-4 col-lg-3">
							<transition-group name="fade-items" tag="div" class="transition-wrapper">
								<RegistrationSub
									v-if="isWaitingRegistration || regData"
									:regData="regData"
									:sessionsRemaining="eventData.sessionsRemaining"
									:costPrefix="eventData.costPrefix"
									:costVaries="eventData.costVaries"
									:costAdvertised="display.costAdvertised"
									:costSuffix="eventData.costSuffix"
									:userAuthenticated="userAuthenticated"
									:isWaitingRegistration="isWaitingRegistration"
									@ctaClicked="handleCtaClicked"
									@reserveCtaClicked="handleReserveCtaClicked"
									key="registrationSub"
								/>
							</transition-group>
						</div>
					</div>
				</div>
			</section>

			<!-- "More" (Next Occurrences) Section -->
			<section v-if="eventData.name && eventData.type && eventData.nextOccurrences && eventData.nextOccurrences.length && !isMobileApp" class="bg-gray-12 b-t">
				<MoreSub
					:eventName="eventData.name"
					:eventType="eventData.type"
					:nextOccurrences="eventData.nextOccurrences"/>
			</section>

		</div>

		<!-- Loading Spinner -->
		<div role="alert" aria-live="assertive">
			<transition name="fade" appear>
				<div class="spinner-overlay" data-testid="spinner" v-show="isWaiting">
					<p class="sr-only">Content is loading…</p>
					<LoadingSpinner />
				</div>
			</transition>
		</div>

		<!-- Cancel Confirmation Modal -->
		<Modal ref="cancelModal">
			<template #title>Cancel Confirmation</template>
			<template #default>
				<div v-if="cancelProgressiveClassMessage" data-testid="progressiveClassMessage" v-html="cancelProgressiveClassMessage"></div>
				<p>Are you sure you want to cancel?</p>
			</template>
			<template #footer>
				<button type="button" class="btn btn-link" @click="cancelModalCta(false)">No</button>
				<button type="button" class="btn btn-primary" @click="cancelModalCta(true)">Yes</button>
			</template>
		</Modal>

		<AddToCalendar ref="addToCalendarComponent" />

	</div>
</template>

<script>
import { defineComponent } from 'vue';
import moment from 'moment';
import axios from 'axios';
import { baseComponentMixin } from '@/mixins/baseComponent.mixin';
import LoadingSpinner from '@/components/schreg-common/LoadingSpinner.vue';
import TemporalDisplay from '@/components/schreg-common/TemporalDisplay.vue';
import AddToCalendar from '@/components/schreg-common/AddToCalendar.vue';
import Modal from '@/components/schreg-common/Modal.vue';
import { NotificationService } from '@/services/notification.service';
import { Helpers } from '@/common/helpers';
import { UxSchedulesApi } from '@/apis/uxSchedules.api';
import { clockObservable$ } from '@/services/clock.service';
import { ClockService, dateProvider, momentProvider } from '@/services/clock.service';
import { StateService } from '@/services/state.service';
import { FrameworkService } from '@/services/framework.service';
import { AnalyticsService } from '@/services/analytics.service';
import RegistrationSub from './sub/RegistrationSub.vue';
import MoreSub from './sub/MoreSub.vue';

let clockSubscription;

export default defineComponent({
	name: 'EventDetailsComponent',
	config: {
		key: 'schreg.eventDetails',
		devPath: 'details',
		exported: true
	},
	components: {
		LoadingSpinner,
		TemporalDisplay,
		Modal,
		AddToCalendar,
		RegistrationSub,
		MoreSub
	},
	mixins: [
		baseComponentMixin
	],
	data() {
		return {
			displayConfig: {
				itemsPerFocusTagsRow: 3,
				itemsPerWhatToKnowTagsRow: 2
			},
			eventId: '',
			eventData: {},
			display: {
				startMoment: momentProvider(),
				endMoment: momentProvider(),
				recursStartLabel: 'Starts',
				recursStartMoment: momentProvider(),
				recursEndLabel: 'Ends',
				recursEndMoment: momentProvider(),
				recursDays: '',
				location: '',
				costAdvertised: ''
			},
			showRegistrationSection: true,
			isWaitingRegistration: true,
			modalContext: null,
			regData: null,
			showAddToCalendar: false,
			endClassTimestamp: 0,
			now: dateProvider(),
			cancelProgressiveClassMessage: '',
			isMobileApp: false
		};
	},
	computed: {
		showCtaSidebar: function() {
			return (this.showRegistrationSection) && !this.eventData.isCanceled && this.endClassTimestamp > this.now;
		},
		focusTagsRowCount: function() {
			return this.eventData.focusTags ? Math.ceil(this.eventData.focusTags.length / this.displayConfig.itemsPerFocusTagsRow) : 0;
		},
		whatToKnowTagsRowCount: function() {
			return this.eventData.whatToKnowTags ? Math.ceil(this.eventData.whatToKnowTags.length / this.displayConfig.itemsPerWhatToKnowTagsRow) : 0;
		}
	},
	created() {
		clockSubscription = clockObservable$
			.subscribe(m => {
				this.now = m;
			});
	},
	async mounted() {
		const stores = FrameworkService.isWorkExperience()
			? ['ProfileStore', 'UrlStore']
			: ['ClubStore', 'ProfileStore', 'UrlStore'];

		this.getFrameworkData(this.completeMounting, stores);
	},
	destroyed() {
		clockSubscription.unsubscribe();
	},
	methods: {
		completeMounting() {
			this.isMobileApp = Helpers.isMobileApp();
			this.eventId = StateService.getQuery('eventId');

			this.isWaiting = true;
			this.isWaitingRegistration = true;

			UxSchedulesApi
				.getEventDetails(this.eventId)
				.then(response => {
					if (!response.ok) {
						this.mountServerError(true, 'We\'re sorry, this class or event is no longer available.', true);
						if (!Helpers.isDevOrUi() && !window.location.pathname.includes('editor.html')) { // Don't redirect locally or when authoring
							window.location.href = '/';
						}
						return;
					}

					this.buildEventDetailsViewData(response);

					AnalyticsService.eventDetailsAnalytics(this.eventData);

					if (this.eventData.isCanceled) {
						return;
					}

					UxSchedulesApi
						.getEventDetailsRegistration(this.eventId)
						.then(registrationResponse => {

							// Note: This will only happen if there is a problem retrieving the registration information, possibly because a service is down
							if (!registrationResponse.ok) {
								NotificationService.info('Registration information failed to load. Please try again.').trigger();
								this.showRegistrationSection = false;
								this.isWaitingRegistration = false;
								return;
							}

							const registration = registrationResponse.data;

							this.showAddToCalendar = // Only show "Add to Calendar" if...
								registration.isRegistered // the user is registered/waitlisted
								|| (!registration.hasOnline && !registration.hasPhone && !registration.hasClub); // OR the event isn't registrable

							if (!registration.hasRegistration) {
								// TODO: maybe gracefully collapse the sidebar?
								this.showRegistrationSection = false;
								this.isWaitingRegistration = false;
								return;
							}

							this.regData = registration;
							this.isWaitingRegistration = false;
						});
				});
		},
		buildEventDetailsViewData(response) {
			// Set up the view data
			this.eventData = response.data;

			this.display.startMoment = moment.parseZone(response.data.start);
			this.display.endMoment = moment.parseZone(response.data.end);

			this.endClassTimestamp = this.display.endMoment.valueOf();

			this.display.recursStartMoment = response.data.recursStart ? moment.parseZone(response.data.recursStart) : null;
			this.display.recursEndMoment = response.data.recursEnd ? moment.parseZone(response.data.recursEnd) : null;
			this.display.recursDays = response.data.recursDays ? response.data.recursDays : null;

			if (this.display.recursStartMoment) {
				this.display.recursStartLabel = this.display.recursStartMoment.isSameOrAfter(momentProvider()) ? 'Starts' : 'Started';
			}

			if (this.display.recursEndMoment) {
				this.display.recursEndLabel = this.display.recursEndMoment.isSameOrAfter(momentProvider()) ? 'Ends' : 'Ended';
			}

			this.display.location = response.data.location;
			if (response.data.cost) {
				this.display.costAdvertised = response.data.cost % 1 === 0 ? response.data.cost.toFixed(0) : response.data.cost.toFixed(2); // Omit cents if a whole dollar
			}

			this.isWaiting = false;

			if (this.eventData.isCanceled) {
				NotificationService.danger('Note: This event has been canceled.').trigger();
				this.showAddToCalendar = false;
			}
		},
		itemCountInFocusTagsRow(index) {
			return this.eventData.focusTags.slice((index - 1) * this.displayConfig.itemsPerFocusTagsRow, index * this.displayConfig.itemsPerFocusTagsRow);
		},
		itemCountInWhatToKnowTagsRow(index) {
			return this.eventData.whatToKnowTags.slice((index - 1) * this.displayConfig.itemsPerWhatToKnowTagsRow, index * this.displayConfig.itemsPerWhatToKnowTagsRow);
		},
		showCancelModal() {
			this.$refs.cancelModal.show();
		},
		closeCancelModal() {
			this.$refs.cancelModal.hide();
		},
		cancelModalCta(doCancel) {
			this.closeCancelModal();

			if (doCancel && this.modalContext) {
				this.isWaitingRegistration = true;
				const registrationId = this.modalContext.registrationId;
				const url = this.modalContext.url;

				this.modalContext = null;
				if (registrationId) {
					this.isWaiting = true;
					let request = {
						method: 'delete',
						url: url,
						headers: {
							'X-LTF-SSOID': StateService.getSSOToken(),
							'ocp-apim-subscription-key': StateService.getApimKey()
						}
					};

					axios.request(request)
						.then(response => {
							if (response.status !== 204) {
								NotificationService.danger('Failed to cancel reservation. Please try again.').trigger();
								this.completeMounting();
							}

							AnalyticsService.reservationCanceledAnalytics(this.eventData.name, this.eventData.upc, this.eventData.locationName, this.eventData.locationId);

							// Pause an extra 2 seconds to give the indexes time to catch up
							ClockService.pause(2000).then(() => {
								this.completeMounting();
							});
						}, () => {
							NotificationService.danger('Failed to cancel reservation. Please try again.').trigger();
							this.completeMounting();
						});
				}
			}
		},
		handleCtaClicked(cta) { // regType
			if (cta.type === 'cancel') {
				this.modalContext = cta;
				this.cancelProgressiveClassMessage = null;

				if (this.eventData.isProgressiveClass) {
					this.cancelProgressiveClassMessage = `<p>Canceling will remove your child from the entire <b>${this.eventData.name}</b> class series.</p><p>If you are unable to attend just one day of the class, please contact the Kids Leader at the club at <a href="mailto:${this.eventData.clubEmail}">${this.eventData.clubEmail}</a>.</p>`;
				}

				this.showCancelModal();
			} else if (cta.url) {
				window.location.href = cta.url;
			}
		},
		handleReserveCtaClicked() {
			AnalyticsService.reservationStartedAnalytics(this.eventData);
		},
		addToCalendar() {
			this.$refs.addToCalendarComponent.open(
				this.eventId, // eventId
				'', // registrationId
				false, // isSpaReservation
				null // reservationDate (needed only for Spa reservations)
			);
		}
	}
});
</script>

<style scoped lang="scss">
@import "@/common/styles/loading.scss";
@import "@/common/styles/btn-block-xs.scss";
@import "styles/eventDetails.scss";
</style>
